import React, { useCallback, useMemo, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import {
  GetShopLocalParticipatingMemberEligibilityQuery,
  ShopLocalCampaignParticipatingMembersInput,
  ShopLocalParticipatingMemberIneligibilityReasonEnum,
} from "../../../graphql/types";
import { makeStyles, FormHelperText } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Loader from "../../common/Loader/Loader";
import { FormikErrors } from "formik";
import { useEnumStrings } from "../../enumStrings";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    fontFamily: "DM Sans, sans-serif",
  },
  searchBox: {
    position: "relative" as const,
    width: "100%",
    marginTop: "8px",
  },
  searchIcon: {
    position: "absolute" as const,
    top: "50%",
    left: "14px",
    transform: "translateY(-50%)",
    color: "#9E9E9E",
    fontSize: "20px",
  },
  searchInput: {
    width: "100%",
    height: "44px",
    padding: "0 14px 0 42px",
    fontSize: "15px",
    fontFamily: "DM Sans, sans-serif",
    border: "1px solid #E0E0E0",
    borderRadius: "10px",
    outline: "none",
    background: "#FFFFFF",
    boxSizing: "border-box" as const,
    "&:focus": {
      borderColor: "#E1A731",
    },
  },
  counterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "14px 0 10px",
    fontSize: "14px",
    color: "#666",
  },
  countNumber: {
    color: "#E1A731",
    fontWeight: 700,
  },
  clearBtn: {
    background: "none",
    border: "none",
    color: "#E1A731",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    padding: 0,
    fontFamily: "DM Sans, sans-serif",
    "&:hover": { textDecoration: "underline" },
  },
  listContainer: {
    border: "1px solid #E0E0E0",
    borderRadius: "10px",
    overflow: "hidden",
    background: "#FFFFFF",
  },
  selectAllRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    background: "#F5F6F7",
    borderBottom: "1px solid #E0E0E0",
    fontFamily: "DM Sans, sans-serif",
  },
  selectAllLabel: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#1F2937",
    marginLeft: "4px",
    flex: 1,
  },
  totalCount: {
    fontSize: "13px",
    color: "#9CA3AF",
  },
  sectionHeader: {
    padding: "8px 14px",
    background: "#F5F6F7",
    borderBottom: "1px solid #E0E0E0",
    borderTop: "1px solid #E0E0E0",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: "#6B7280",
    fontFamily: "DM Sans, sans-serif",
  },
  selectedItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    background: "#FFFFFF",
    borderLeft: "3px solid #E1A731",
    borderBottom: "1px solid #F0F0F0",
    cursor: "pointer",
    fontFamily: "DM Sans, sans-serif",
    "&:hover": { background: "#FAFAFA" },
    "&:last-child": { borderBottom: "none" },
  },
  selectedIneligibleItem: {
    borderLeftColor: "#C62828",
    background: "#FFF8F8",
  },
  memberItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    borderBottom: "1px solid #F0F0F0",
    cursor: "pointer",
    fontFamily: "DM Sans, sans-serif",
    "&:hover": { background: "#FAFAFA" },
    "&:last-child": { borderBottom: "none" },
  },
  itemContent: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: "3px",
    marginLeft: "4px",
    minWidth: 0,
  },
  itemLabel: {
    fontSize: "15px",
    color: "#1F2937",
    fontWeight: 600,
  },
  ineligibleLabel: {
    color: "#B71C1C",
    fontSize: "12px",
    fontWeight: 700,
  },
  ineligibleReason: {
    color: "#8A3030",
    fontSize: "12px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "28px 16px",
    gap: "4px",
    background: "#FFFFFF",
    textAlign: "center" as const,
  },
  emptyTitle: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#1F2937",
  },
  emptyHint: {
    fontSize: "13px",
    color: "#6B7280",
  },
  checkbox: {
    padding: "4px",
    color: "#D1D5DB !important",
    "&.Mui-checked": { color: "#E1A731 !important" },
    "&.MuiCheckbox-indeterminate": { color: "#E1A731 !important" },
  },
  scrollList: {
    maxHeight: "320px",
    overflowY: "auto" as const,
  },
}));

type EligibilityItem = {
  id: string;
  name: string;
  eligible: boolean;
  ineligibilityReasons: ShopLocalParticipatingMemberIneligibilityReasonEnum[];
};

export default function ChooseParticipatingMembers({
  selectedChamberMembers,
  handleChamberMembersChange,
  eligibilityData,
  eligibilityLoading,
  eligibilityError,
  contestDatesAreValid,
  formikErrorsAvailableToChamberIds,
}: {
  selectedChamberMembers: ShopLocalCampaignParticipatingMembersInput[];
  handleChamberMembersChange: (
    e: ShopLocalCampaignParticipatingMembersInput[]
  ) => void;
  eligibilityData:
    | GetShopLocalParticipatingMemberEligibilityQuery
    | undefined;
  eligibilityLoading: boolean;
  eligibilityError: boolean;
  contestDatesAreValid: boolean;
  formikErrorsAvailableToChamberIds:
    | string
    | string[]
    | FormikErrors<ShopLocalCampaignParticipatingMembersInput>[]
    | undefined;
}) {
  const classes = useStyles();
  const [searchInput, setSearchInput] = useState("");

  const allItems = useMemo<EligibilityItem[]>(
    () =>
      (eligibilityData?.getShopLocalParticipatingMemberEligibility || []).map(
        (member) => ({
          id: member.memberId,
          name: member.memberName,
          eligible: member.eligible,
          ineligibilityReasons: member.ineligibilityReasons,
        })
      ),
    [eligibilityData?.getShopLocalParticipatingMemberEligibility]
  );
  const itemById = useMemo(
    () => new Map(allItems.map((item) => [item.id, item])),
    [allItems]
  );
  const eligibleItems = useMemo(
    () => allItems.filter((item) => item.eligible),
    [allItems]
  );
  const eligibleIdSet = useMemo(
    () => new Set(eligibleItems.map((item) => item.id)),
    [eligibleItems]
  );
  const selectedIdSet = useMemo(
    () => new Set(selectedChamberMembers.map((member) => member.id)),
    [selectedChamberMembers]
  );
  const selectedItems = useMemo<EligibilityItem[]>(
    () =>
      selectedChamberMembers.map(({ id }) =>
        itemById.get(id) || {
          id,
          name: "Member no longer available",
          eligible: false,
          ineligibilityReasons: [],
        }
      ),
    [itemById, selectedChamberMembers]
  );

  const normalizedSearch = searchInput.trim().toLowerCase();
  const visibleSelectedItems = useMemo(
    () =>
      selectedItems.filter((item) =>
        item.name.toLowerCase().includes(normalizedSearch)
      ),
    [normalizedSearch, selectedItems]
  );
  const availableItems = useMemo(
    () =>
      eligibleItems.filter(
        (item) =>
          !selectedIdSet.has(item.id) &&
          item.name.toLowerCase().includes(normalizedSearch)
      ),
    [eligibleItems, normalizedSearch, selectedIdSet]
  );

  const handleToggle = useCallback(
    (newCheckState: boolean, memberId: string) => () => {
      const nextSelected = newCheckState
        ? [...selectedChamberMembers, { id: memberId }]
        : selectedChamberMembers.filter((member) => member.id !== memberId);
      handleChamberMembersChange(nextSelected);
    },
    [handleChamberMembersChange, selectedChamberMembers]
  );

  const selectedEligibleCount = selectedChamberMembers.filter((member) =>
    eligibleIdSet.has(member.id)
  ).length;
  const allEligibleSelected =
    eligibleItems.length > 0 && selectedEligibleCount === eligibleItems.length;
  const someEligibleSelected =
    selectedEligibleCount > 0 && !allEligibleSelected;

  const handleToggleAll = useCallback(() => {
    const selectedIneligibleMembers = selectedChamberMembers.filter(
      (member) => !eligibleIdSet.has(member.id)
    );
    handleChamberMembersChange(
      allEligibleSelected
        ? selectedIneligibleMembers
        : [
            ...selectedIneligibleMembers,
            ...eligibleItems.map(({ id }) => ({ id })),
          ]
    );
  }, [
    allEligibleSelected,
    eligibleIdSet,
    eligibleItems,
    handleChamberMembersChange,
    selectedChamberMembers,
  ]);

  const getReasonText = useCallback(
    (reason: ShopLocalParticipatingMemberIneligibilityReasonEnum) => {
      switch (reason) {
        case ShopLocalParticipatingMemberIneligibilityReasonEnum.MemberNotAppVisible:
          return "not visible in the app";
        case ShopLocalParticipatingMemberIneligibilityReasonEnum.MemberSuspended:
          return "member is suspended";
        case ShopLocalParticipatingMemberIneligibilityReasonEnum.NoEligiblePerk:
          return "no perk is available during these contest dates";
        default:
          return "no longer eligible";
      }
    },
    []
  );

  if (!contestDatesAreValid) {
    return (
      <div className={classes.listContainer}>
        <div className={classes.emptyState}>
          <div className={classes.emptyTitle}>Choose valid contest dates first</div>
          <div className={classes.emptyHint}>
            Eligible participating members will appear after both dates are set.
          </div>
        </div>
      </div>
    );
  }

  if (eligibilityLoading) {
    return (
      <div style={{ marginTop: "46px" }}>
        <Loader />
      </div>
    );
  }

  if (eligibilityError) {
    return (
      <div className={classes.listContainer}>
        <div className={classes.emptyState}>
          <div className={classes.emptyTitle}>Unable to load eligible members</div>
          <div className={classes.emptyHint}>
            Check the contest dates and try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.searchBox}>
        <SearchIcon className={classes.searchIcon} />
        <input
          type="text"
          className={classes.searchInput}
          placeholder="Search by member company name"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
      </div>

      <div className={classes.counterRow}>
        <span>
          <span className={classes.countNumber}>
            {selectedChamberMembers.length}
          </span>{" "}
          selected · {eligibleItems.length} eligible companies
        </span>
        {selectedChamberMembers.length > 0 && (
          <button
            type="button"
            className={classes.clearBtn}
            onClick={() => handleChamberMembersChange([])}
          >
            Clear
          </button>
        )}
      </div>

      <div className={classes.listContainer}>
        <div
          className={classes.selectAllRow}
          onClick={eligibleItems.length > 0 ? handleToggleAll : undefined}
          style={{ cursor: eligibleItems.length > 0 ? "pointer" : "default" }}
        >
          <Checkbox
            className={classes.checkbox}
            checked={allEligibleSelected}
            indeterminate={someEligibleSelected}
            disabled={eligibleItems.length === 0}
            disableRipple
          />
          <span className={classes.selectAllLabel}>Select all eligible</span>
          <span className={classes.totalCount}>
            {eligibleItems.length} companies
          </span>
        </div>

        <div className={classes.sectionHeader}>SELECTED MEMBERS</div>
        {visibleSelectedItems.length === 0 ? (
          <div className={classes.emptyState}>
            <div className={classes.emptyTitle}>
              {selectedItems.length === 0
                ? "No members selected yet"
                : "No members found"}
            </div>
            {selectedItems.length === 0 && (
              <div className={classes.emptyHint}>
                Check a box below to add a member to this contest.
              </div>
            )}
          </div>
        ) : (
          <div className={classes.scrollList}>
            {visibleSelectedItems.map((item) => (
              <div
                key={`selected-${item.id}`}
                className={`${classes.selectedItem} ${
                  item.eligible ? "" : classes.selectedIneligibleItem
                }`}
                onClick={handleToggle(false, item.id)}
              >
                <Checkbox className={classes.checkbox} checked disableRipple />
                <span className={classes.itemContent}>
                  <span className={classes.itemLabel}>{item.name}</span>
                  {!item.eligible && (
                    <>
                      <span className={classes.ineligibleLabel}>
                        No longer eligible — remove before saving
                      </span>
                      <span className={classes.ineligibleReason}>
                        {item.ineligibilityReasons.length > 0
                          ? item.ineligibilityReasons
                              .map(getReasonText)
                              .join(", ")
                          : "member record is no longer available"}
                      </span>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className={classes.sectionHeader}>ELIGIBLE MEMBERS</div>
        <div className={classes.scrollList}>
          {availableItems.length === 0 ? (
            <div className={classes.emptyState}>
              <div className={classes.emptyTitle}>
                {eligibleItems.length === 0
                  ? "No eligible members for these contest dates"
                  : "No members found"}
              </div>
              {eligibleItems.length === 0 && (
                <div className={classes.emptyHint}>
                  A member must be visible in the app, not suspended, and have
                  a perk available during the contest.
                </div>
              )}
            </div>
          ) : (
            availableItems.map((item) => (
              <div
                key={`available-${item.id}`}
                className={classes.memberItem}
                onClick={handleToggle(true, item.id)}
              >
                <Checkbox
                  className={classes.checkbox}
                  checked={false}
                  disableRipple
                />
                <span className={classes.itemLabel}>{item.name}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <FormHelperText
        component="legend"
        style={{
          color: "#f44336",
          paddingTop: "8px",
          textAlign: "left",
        }}
      >
        {typeof formikErrorsAvailableToChamberIds === "string"
          ? formikErrorsAvailableToChamberIds
          : null}
      </FormHelperText>
    </div>
  );
}

export const ParticipationCodeExplanationText = () => {
  const enumStrings = useEnumStrings();
  return (
    <h4>
      {enumStrings.contestParticipationCode.description}
      <br />
      <br />
      {enumStrings.contestParticipationCode.instructions.Instruction1}
      <br />
      <br />
      {enumStrings.contestParticipationCode.instructions.Instruction2}
      <br />
      <br />
      {enumStrings.contestParticipationCode.instructions.Instruction3}
      <br />
      <br />
      {enumStrings.contestParticipationCode.instructions.Instruction4}
    </h4>
  );
};
